import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, ArrowRight, Check, Lightbulb, Upload, MonitorPlay, Image as ImageIcon, Type,
  MousePointerClick, Tag, Star, HelpCircle, Clock, Mail, Images, LayoutGrid, Minus,
  MoveVertical, Link2, Sparkles, ShieldCheck, ThumbsUp, AtSign, MessageCircle, Phone,
  Link as LinkIcon, Package, Rocket, LayoutTemplate, Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthContext } from '../contexts/AuthContext';

const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = '#0E3A40';

const blockTypes = [
  { icon: MonitorPlay, label: 'YouTube', desc: 'ฝังวิดีโอจาก YouTube', bg: 'rgba(255,122,69,0.1)', color: '#B3552A' },
  { icon: ImageIcon, label: 'เพิ่มรูปภาพ', desc: 'อัปโหลดหรือใส่ URL', bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
  { icon: Type, label: 'เพิ่มข้อความ', desc: 'ข้อความอธิบาย', bg: 'rgba(58,147,185,0.1)', color: '#276E8A' },
  { icon: MousePointerClick, label: 'ปุ่ม CTA', desc: 'ปุ่มกระตุ้นการขาย', bg: 'rgba(91,111,203,0.1)', color: '#4A57A8' },
  { icon: Tag, label: 'ราคาสินค้า', desc: 'แสดงราคาสินค้า', bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
  { icon: Star, label: 'รีวิว', desc: 'รีวิวจากลูกค้า', bg: 'rgba(58,147,185,0.1)', color: '#276E8A' },
  { icon: HelpCircle, label: 'FAQ', desc: 'คำถามที่พบบ่อย', bg: 'rgba(91,111,203,0.1)', color: '#4A57A8' },
  { icon: Clock, label: 'นับถอยหลัง', desc: 'สร้างความเร่งด่วน', bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
  { icon: Mail, label: 'ฟอร์มติดต่อ', desc: 'รับข้อมูลลูกค้า', bg: 'rgba(58,147,185,0.1)', color: '#276E8A' },
  { icon: Images, label: 'แกลเลอรี่', desc: 'แสดงภาพหลายรูป', bg: 'rgba(91,111,203,0.1)', color: '#4A57A8' },
  { icon: LayoutGrid, label: 'ตารางเปรียบเทียบ', desc: 'เปรียบเทียบแพ็คเกจ', bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
  { icon: Lightbulb, label: 'ไอคอน + ข้อความ', desc: 'จุดเด่นสินค้า', bg: 'rgba(58,147,185,0.1)', color: '#276E8A' },
  { icon: Minus, label: 'เส้นแบ่ง', desc: 'แบ่งส่วนเนื้อหา', bg: 'rgba(14,58,64,0.06)', color: '#5B7D80' },
  { icon: MoveVertical, label: 'ช่องว่าง', desc: 'เว้นระยะห่าง', bg: 'rgba(14,58,64,0.06)', color: '#5B7D80' },
];

const subGuides = [
  {
    icon: Upload, bg: 'rgba(58,147,185,0.1)', color: '#276E8A',
    title: 'วิธีอัปโหลดวิดีโอ', intro: 'อัปโหลดวิดีโอไฟล์ได้เลยจากอุปกรณ์ของคุณ:',
    steps: [
      { n: 1, title: 'เลือกประเภท "เพิ่มวิดีโอ"' },
      { n: 2, title: 'คลิกเลือกไฟล์' },
      { n: 3, title: 'รอการอัปโหลด' },
      { n: 4, title: 'วิดีโอพร้อมใช้งาน' },
    ],
    noteTitle: 'ข้อจำกัด',
    noteLines: ['จำนวนคลิปที่อัปโหลดได้ขึ้นอยู่กับแพ็คเกจของคุณ', 'รองรับอัตราส่วน 16:9 (แนะนำที่สุด)', 'รองรับไฟล์ MP4, MOV, AVI', 'ความเร็วอัปโหลดขึ้นอยู่กับอินเทอร์เน็ต'],
  },
  {
    icon: MonitorPlay, bg: 'rgba(255,122,69,0.1)', color: '#B3552A',
    title: 'วิธีวางลิงก์วิดีโอจาก YouTube', intro: 'ใช้ลิงก์วิดีโอจาก YouTube แทนการอัปโหลด:',
    steps: [
      { n: 1, title: 'เลือกประเภท "YouTube"' },
      { n: 2, title: 'คัดลอกลิงก์จาก YouTube', detail: 'บน YouTube กด "แชร์" แล้วคัดลอกลิงก์' },
      { n: 3, title: 'วางลิงก์', detail: 'วางในช่องแปะลิงก์ แล้วกด Enter หรือ "ยืนยัน"' },
      { n: 4, title: 'วิดีโอพร้อมใช้งาน' },
    ],
    noteTitle: 'ข้อดี',
    noteLines: ['ไม่กินพื้นที่จัดเก็บของคุณ · เพิ่มได้ไม่จำกัดจำนวน'],
  },
  {
    icon: Link2, bg: 'rgba(0,180,161,0.1)', color: '#0E7C86',
    title: 'วิธีวางลิงก์รูปภาพ', intro: 'แทนการอัปโหลด คุณสามารถวางลิงก์รูปภาพจาก URL ได้:',
    steps: [
      { n: 1, title: 'เลือกประเภท "เพิ่มรูปภาพ"' },
      { n: 2, title: 'หาลิงก์รูปภาพ', detail: 'คลิกขวาที่รูป แล้วเลือก "คัดลอกที่อยู่รูปภาพ"' },
      { n: 3, title: 'คัดลอกลิงก์', detail: 'ลิงก์ควรลงท้ายด้วย .jpg .png หรือ .gif' },
      { n: 4, title: 'วางลิงก์แล้วกดยืนยัน' },
      { n: 5, title: 'รูปภาพพร้อมใช้งาน' },
    ],
    noteTitle: 'แหล่งรูปภาพแนะนำ',
    noteLines: ['Google Drive (ตั้งสิทธิ์ Public)', 'Imgur, ImgBB (ฟรีถาวร)', 'Cloudinary (ฟรี Plan)'],
  },
];

const textOptions = ['ตัวหนา / ตัวเอียง / ขีดเส้นใต้', 'เลือกขนาดตัวอักษร (12px - 48px)', 'เลือกสีตัวอักษร', 'จัดตำแหน่ง (ซ้าย/กลาง/ขวา)'];
const previewNotes = ['เห็นหน้าตาในแบบเดียวกับที่ลูกค้าจะเห็นจริง', 'แสดงผลตามความสูงจริงของอุปกรณ์', 'ไม่มีปุ่มแก้ไขปะปนอยู่'];
const contactChannels = [
  { icon: ThumbsUp, label: 'Facebook', color: '#276E8A' },
  { icon: AtSign, label: 'Instagram', color: '#4A57A8' },
  { icon: MessageCircle, label: 'LINE', color: '#0E7C86' },
  { icon: Phone, label: 'Phone', color: '#B3552A' },
];
const features = [
  { icon: Sparkles, title: 'ไอคอนสวยงามจาก Lucide React', desc: 'เปลี่ยนจากอิโมจิเป็นไอคอนที่ทันสมัยกว่าทั่วทั้งเครื่องมือ' },
  { icon: LinkIcon, title: 'วางลิงก์รูปภาพได้แล้ว', desc: 'ไม่ต้องอัปโหลด วางลิงก์รูปภาพจาก URL ได้เลย ประหยัดพื้นที่จัดเก็บ' },
  { icon: Package, title: 'ระบบจำกัดจำนวน Sale Page', desc: 'แต่ละบัญชีเผยแพร่ได้ 1 Sale Page ต่อการสมัคร 1 แพ็คเกจ ป้องกันการสร้างเกินโควต้า' },
  { icon: Rocket, title: 'สร้างและทดสอบได้ฟรี', desc: 'สร้าง Sale Page ทดสอบระบบได้ฟรีไม่จำกัดเวลา จ่ายเงินเมื่อพร้อมเผยแพร่จริงเท่านั้น' },
  { icon: LayoutTemplate, title: 'UI/UX ที่ดีขึ้น', desc: 'ป้ายกำกับ "Sale Page" ชัดเจนขึ้น พร้อมแก้ไขจุดที่เคยใช้งานยาก' },
];
const keyTips = [
  { n: 1, text: 'อัปโหลดรูปภาพขนาดแนะนำ 1200x800 พิกเซล' },
  { n: 2, text: 'ตั้งชื่อสินค้าที่ชัดเจน เข้าใจง่าย ไม่กำกวม' },
  { n: 3, text: 'ใช้ลิงก์ YouTube สำหรับวิดีโอ เพื่อประหยัดพื้นที่จัดเก็บ' },
  { n: 4, text: 'ทดสอบทุกลิงก์ทั้งภาพและวิดีโอก่อนบันทึก' },
  { n: 5, text: 'ตรวจสอบหน้าตัวอย่างให้แน่ใจก่อนเผยแพร่' },
];

const StepSection: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <section style={{ background: '#fff', borderRadius: 20, padding: 'clamp(18px,2.4vw,22px)', border: '1px solid rgba(14,58,64,0.12)', boxShadow: '0 1px 2px rgba(14,58,64,0.03)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
      <span style={{ width: 30, height: 30, borderRadius: 999, background: 'linear-gradient(135deg, #00B4A1, #3A93B9)', color: '#fff', fontFamily: HEAD, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>{n}</span>
      <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 16.5 }}>{title}</span>
    </div>
    {children}
  </section>
);

const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', gap: 9, background: 'rgba(0,180,161,0.13)', borderRadius: 13, padding: '11px 14px' }}>
    <Lightbulb style={{ width: 16, height: 16, color: '#0E7C86', flex: '0 0 auto', marginTop: 1 }} />
    <span style={{ fontSize: 12.5, color: '#083E42', fontWeight: 500, lineHeight: 1.55 }}>{children}</span>
  </div>
);

const SalePageHelp: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [existingSalepage, setExistingSalepage] = useState<{ id: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkExistingSalepage = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      try {
        const q = query(collection(db, 'salepages'), where('userId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) setExistingSalepage({ id: querySnapshot.docs[0].id });
      } catch (error) {
        console.error('Error checking salepage:', error);
      } finally {
        setLoading(false);
      }
    };
    checkExistingSalepage();
  }, [user?.uid]);

  const handleNavigateToBuilder = () => {
    if (existingSalepage) navigate(`/sale-page?salePageId=${existingSalepage.id}`);
    else navigate('/sale-page');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F8F8', color: INK, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>

      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(243,248,248,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(14,58,64,0.08)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '14px clamp(16px,3vw,24px)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" onClick={() => navigate('/sale-page')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#5B7D80', background: 'transparent', border: 0, cursor: 'pointer', padding: 0, fontFamily: FONT }}>
            <ArrowLeft style={{ width: 16, height: 16 }} /> กลับไปหน้า Sale Page
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 760, margin: '0 auto', width: '100%', padding: 'clamp(8px,2vw,16px) clamp(16px,3vw,24px) clamp(56px,7vw,84px)', display: 'flex', flexDirection: 'column', gap: 14 }}>

        <div style={{ marginBottom: 6 }}>
          <h1 style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 'clamp(22px,2.6vw,28px)', margin: '0 0 5px', letterSpacing: '-0.02em' }}>วิธีใช้งาน Sale Page Builder</h1>
          <p style={{ fontSize: 13.5, color: '#5B7D80', margin: 0 }}>คู่มือการใช้งานแบบละเอียด พร้อมตัวอย่างการใช้งานจริง</p>
        </div>

        <StepSection n={1} title="เริ่มต้นสร้างหน้าขาย">
          <p style={{ fontSize: 13.5, color: '#3C6367', margin: '0 0 12px', lineHeight: 1.6 }}>หลักการ "คิดแบบนักขาย" เขียนแบบง่ายๆ เข้าใจง่ายและตรงจุด</p>
          <TipBox>แนะนำ: เลือกประเด็นที่คุณอยากจะเสนอให้ลูกค้าเห็นก่อน เช่น ได้ในราคาถูกกว่าท้องตลาด</TipBox>
        </StepSection>

        <StepSection n={2} title="เลือกประเภทเนื้อหา">
          <p style={{ fontSize: 13.5, color: '#3C6367', margin: '0 0 12px' }}>มีหลายประเภทให้เลือก:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 8, marginBottom: 16 }}>
            {blockTypes.map((bt) => {
              const Icon = bt.icon;
              return (
                <div key={bt.label} style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#F3F8F8', border: '1px solid rgba(14,58,64,0.08)', borderRadius: 12, padding: '10px 12px' }}>
                  <span style={{ width: 28, height: 28, borderRadius: 9, background: bt.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                    <Icon style={{ width: 14, height: 14, color: bt.color }} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: INK }}>{bt.label}</div>
                    <div style={{ fontSize: 11, color: '#5B7D80', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bt.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {subGuides.map((g) => {
              const GIcon = g.icon;
              return (
                <div key={g.title} style={{ border: '1px solid rgba(14,58,64,0.12)', borderRadius: 16, padding: 'clamp(14px,1.8vw,17px)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 9, background: g.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                      <GIcon style={{ width: 14, height: 14, color: g.color }} />
                    </span>
                    <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14.5 }}>{g.title}</span>
                  </div>
                  <p style={{ fontSize: 12.5, color: '#5B7D80', margin: '0 0 10px' }}>{g.intro}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 10 }}>
                    {g.steps.map((s) => (
                      <div key={s.n} style={{ display: 'flex', gap: 9, fontSize: 12.5, color: '#3C6367', lineHeight: 1.5 }}>
                        <span style={{ width: 17, height: 17, borderRadius: 999, background: '#EAF1F1', color: '#3C6367', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', marginTop: 1 }}>{s.n}</span>
                        <span><b style={{ color: INK }}>{s.title}</b>{(s as any).detail && ` — ${(s as any).detail}`}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: 'rgba(0,180,161,0.13)', borderRadius: 12, padding: '10px 13px' }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#0E7C86', marginBottom: 4 }}>{g.noteTitle}</div>
                    {g.noteLines.map((nl) => (
                      <div key={nl} style={{ fontSize: 11.5, color: '#3C6367', lineHeight: 1.55 }}>{nl}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </StepSection>

        <StepSection n={3} title="แต่งข้อความ">
          <p style={{ fontSize: 13.5, color: '#3C6367', margin: '0 0 10px' }}>เมื่อเพิ่มข้อความแล้ว คุณสามารถเลือกข้อความแล้วตั้งค่าได้:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 12 }}>
            {textOptions.map((t) => (
              <div key={t} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#3C6367' }}>
                <Check style={{ width: 14, height: 14, color: '#00B4A1', flex: '0 0 auto', marginTop: 2 }} /> {t}
              </div>
            ))}
          </div>
          <TipBox>เคล็ดลับ: เลือกขนาดตัวอักษรที่เหมาะสมและอ่านง่ายบนโทรศัพท์มือถือด้วย</TipBox>
        </StepSection>

        <StepSection n={4} title="จัดเรียงการ์ด">
          <p style={{ fontSize: 13.5, color: '#3C6367', margin: '0 0 12px' }}>ใช้ปุ่มควบคุมที่มุมของการ์ดแต่ละใบ:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#F3F8F8', borderRadius: 999, padding: '8px 15px', fontSize: 12.5, color: INK }}>
              <MoveVertical style={{ width: 14, height: 14, color: '#3A93B9' }} /> เลื่อนขึ้น/ลง
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#F3F8F8', borderRadius: 999, padding: '8px 15px', fontSize: 12.5, color: INK }}>
              <Trash2 style={{ width: 14, height: 14, color: '#B3552A' }} /> ลบการ์ด
            </span>
          </div>
        </StepSection>

        <StepSection n={5} title="เลือกสีพื้นหลัง">
          <p style={{ fontSize: 13.5, color: '#3C6367', margin: '0 0 12px' }}>คลิกที่ "เลือกสีพื้นหลัง" ในแถบเครื่องมือด้านบน เพื่อเปลี่ยนสีพื้นหลังของหน้าขาย (หรือกดวงกลมลายรุ้งเพื่อเลือกสีเองได้อิสระ)</p>
          <div style={{ height: 16, borderRadius: 999, background: 'linear-gradient(90deg, #FF7A45, #FFC15E, #00B4A1, #3A93B9, #5B6FCB)' }} />
        </StepSection>

        <StepSection n={6} title="ดูตัวอย่าง">
          <p style={{ fontSize: 13.5, color: '#3C6367', margin: '0 0 10px' }}>กดปุ่ม "ดูตัวอย่าง" เพื่อดูหน้าที่คุณสร้างในแบบที่ลูกค้าจะเห็นจริง:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {previewNotes.map((p) => (
              <div key={p} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#3C6367' }}>
                <Check style={{ width: 14, height: 14, color: '#00B4A1', flex: '0 0 auto', marginTop: 2 }} /> {p}
              </div>
            ))}
          </div>
        </StepSection>

        <StepSection n={7} title="เพิ่มช่องทางติดต่อ">
          <p style={{ fontSize: 13.5, color: '#3C6367', margin: '0 0 12px' }}>สร้างช่องทางให้ลูกค้าติดต่อคุณเพิ่มเติมนอกจาก Social Media:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 12 }}>
            {contactChannels.map((c) => {
              const CIcon = c.icon;
              return (
                <span key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#F3F8F8', borderRadius: 999, padding: '8px 15px', fontSize: 12.5, color: INK }}>
                  <CIcon style={{ width: 14, height: 14, color: c.color }} /> {c.label}
                </span>
              );
            })}
          </div>
          <TipBox>แนะนำ: เพิ่มช่องทางติดต่อที่ลูกค้าเข้าถึงง่ายและคุณตอบกลับได้เร็ว</TipBox>
        </StepSection>

        <section style={{ background: '#0E3A40', borderRadius: 22, padding: 'clamp(20px,2.6vw,26px)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 15% 0%, rgba(0,180,161,0.35), transparent 55%), radial-gradient(circle at 100% 100%, rgba(91,111,203,0.3), transparent 55%)' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
              <Sparkles style={{ width: 19, height: 19, color: '#4FE0CC' }} />
              <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 17, color: '#fff' }}>ฟีเจอร์ใหม่ล่าสุด</span>
            </div>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)', margin: '0 0 16px' }}>อัปเดตล่าสุดที่ช่วยให้การสร้างหน้าง่ายขึ้น</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {features.map((f) => {
                const FIcon = f.icon;
                return (
                  <div key={f.title} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '13px 15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5 }}>
                      <FIcon style={{ width: 15, height: 15, color: '#4FE0CC' }} />
                      <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 13.5, color: '#fff' }}>{f.title}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{f.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section style={{ background: '#0E3A40', borderRadius: 22, padding: 'clamp(20px,2.6vw,26px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
            <ShieldCheck style={{ width: 19, height: 19, color: '#4FE0CC' }} />
            <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 17, color: '#fff' }}>เคล็ดลับสำคัญ</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {keyTips.map((k) => (
              <div key={k.n} style={{ display: 'flex', gap: 11 }}>
                <span style={{ width: 22, height: 22, borderRadius: 999, background: 'rgba(79,224,204,0.16)', color: '#4FE0CC', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>{k.n}</span>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>{k.text}</span>
              </div>
            ))}
          </div>
        </section>

        <button
          type="button"
          onClick={handleNavigateToBuilder}
          disabled={loading}
          style={{
            alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 999,
            padding: '13px 26px', fontFamily: HEAD, fontSize: 14.5, fontWeight: 600,
            boxShadow: '0 16px 32px -16px rgba(0,180,161,0.55)', marginTop: 6,
            cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'กำลังโหลด...' : existingSalepage ? 'สร้าง Sale Page ต่อ' : 'เริ่มสร้าง Sale Page'} <ArrowRight style={{ width: 16, height: 16 }} />
        </button>

      </main>
    </div>
  );
};

export default SalePageHelp;
